/**
 * Global Search Hook
 * Provides search functionality using GlobalSearchService with state persistence
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { serviceFactory } from '@/services/core/serviceFactory.ts';
import { GlobalSearchService, type PatientSearchResult } from '@/services/features/GlobalSearchService.ts';

// Constants for sessionStorage keys
const SEARCH_STATE_KEY = 'globalSearchState';

// Interface for persisted search state
interface PersistedSearchState {
  searchBy: string;
  searchTerm: string;
  rowsPerPage: number;
  hasSearched: boolean;
}

export interface UseGlobalSearchReturn {
  patients: PatientSearchResult[];
  isLoading: boolean;
  searchBy: string;
  searchTerm: string;
  currentPage: number;
  rowsPerPage: number;
  totalCount: number;
  setSearchBy: (value: string) => void;
  setSearchTerm: (value: string) => void;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  handleSearch: (e: React.FormEvent) => void;
  handleReload: () => void;
  loadDefaultData: () => Promise<void>;
  searchPatients: (searchStr: string, field: string) => Promise<void>;
}

// Utility functions for sessionStorage management
const saveSearchState = (state: PersistedSearchState) => {
  try {
    sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save search state to sessionStorage:', error);
  }
};

const loadSearchState = (): PersistedSearchState | null => {
  try {
    const saved = sessionStorage.getItem(SEARCH_STATE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.warn('Failed to load search state from sessionStorage:', error);
    return null;
  }
};


const clearPersistedSearchData = () => {
  try {
    sessionStorage.removeItem(SEARCH_STATE_KEY);
    // No longer storing results, so no need to clear them
  } catch (error) {
    console.warn('Failed to clear persisted search data:', error);
  }
};

export const useGlobalSearch = (): UseGlobalSearchReturn => {
  const { hasuraToken, user } = useAuth();
  const { toast } = useToast();
  
  // Initialize state with empty array - don't load results from storage
  const [patients, setPatients] = useState<PatientSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Load persisted search criteria only (not results)
  const persistedState = loadSearchState();
  const [searchBy, setSearchBy] = useState(persistedState?.searchBy || 'full_name');
  const [searchTerm, setSearchTerm] = useState(persistedState?.searchTerm || '');
  const [currentPage, setCurrentPage] = useState(1); // Always start from page 1
  const [rowsPerPage, setRowsPerPage] = useState(persistedState?.rowsPerPage || 10);
  const [hasSearched, setHasSearched] = useState(false); // Always start as not searched
  const [isInitialized, setIsInitialized] = useState(false);

  // Get service instance
  const globalSearchService = serviceFactory.getService(GlobalSearchService);

  // Mark as initialized after first render to prevent clearing persisted data
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Save search criteria to sessionStorage - only save if search term is not empty and user has searched
  useEffect(() => {
    if (isInitialized && searchTerm.trim() && hasSearched) {
      const state: PersistedSearchState = {
        searchBy,
        searchTerm,
        rowsPerPage,
        hasSearched: true
      };
      saveSearchState(state);
    }
  }, [searchBy, searchTerm, rowsPerPage, hasSearched, isInitialized]);

  // Load default data on component mount only if no persisted search exists
  useEffect(() => {
    if (hasuraToken) {
      // Only load default data if we don't have a persisted search term
      const persistedState = loadSearchState();
      if (!persistedState?.searchTerm?.trim()) {
        loadDefaultData();
      }
    }
  }, [hasuraToken]);

  // Re-execute search if we have persisted search criteria
  useEffect(() => {
    if (hasuraToken && isInitialized && persistedState?.searchTerm?.trim()) {
      // Re-run the search with persisted criteria
      searchPatients(persistedState.searchTerm, persistedState.searchBy);
    }
  }, [hasuraToken, isInitialized]);

  // Cleanup effect - clear storage only on tab close/refresh, not on navigation
  useEffect(() => {
    // Handle beforeunload (tab close/refresh)
    const handleBeforeUnload = () => {
      clearPersistedSearchData();
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function - only remove event listeners, don't clear storage
    return () => {
      // Remove event listeners but keep search data for navigation
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const loadDefaultData = async () => {
    if (!hasuraToken || !user?.id || !user?.type) return;

    setIsLoading(true);
    try {
      // All users use server-side pagination with pep_vw_patient_details
      const offset = (currentPage - 1) * rowsPerPage;
      const limit = rowsPerPage;
      
      const response = await globalSearchService.getPatientsByUserRole(
        Number(user.id),
        user.type,
        offset,
        limit
      );

      if (response.success && response.data?.pep_vw_consolidated_data) {
        const resultData = response.data.pep_vw_consolidated_data;
        setPatients(resultData as PatientSearchResult[]);
        setTotalCount(response.data.totalCount || resultData.length);
      } else {
        throw new Error(response.errors?.[0] || 'Failed to load default data');
      }
    } catch (err) {
      toast({
        title: "Load Failed",
        description: "Failed to load patient data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const searchPatients = async (searchStr: string, field: string) => {
    if (!hasuraToken || !user?.id || !user?.type) {
      toast({
        title: "Authentication Required",
        description: "Please log in to view patient records.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      let response;
      const searchPattern = `%${searchStr}%`;

      // Use server-side search based on field type - API uses search_string with _eq and %pattern%
      switch (field) {
        case 'full_name':
        case 'name':
          response = await globalSearchService.searchPatientsBySpecificField(searchPattern, 'full_name');
          break;
        case 'dob':
          response = await globalSearchService.searchPatientsByDOB(searchStr);
          break;
        case 'mobile_phone_number':
          response = await globalSearchService.searchPatientsBySpecificField(searchPattern, 'mobile_phone_number');
          break;
        case 'home_phone_number':
          response = await globalSearchService.searchPatientsBySpecificField(searchPattern, 'home_phone_number');
          break;
        case 'email':
          response = await globalSearchService.searchPatientsBySpecificField(searchPattern, 'email');
          break;
        case 'clinic_name':
        case 'clinic':
          response = await globalSearchService.searchPatientsBySpecificField(searchPattern, 'clinic_name');
          break;
        case 'emr_id':
        case 'emr':
          response = await globalSearchService.searchPatientsBySpecificField(searchPattern, 'emr_id');
          break;
        default:
          response = await globalSearchService.searchPatients(searchPattern);
          break;
      }

      if (response.success && response.data?.pep_vw_consolidated_data) {
        const searchResults = response.data.pep_vw_consolidated_data as PatientSearchResult[];
        
        setPatients(searchResults);
        setTotalCount(searchResults.length);
        setCurrentPage(1);
        setHasSearched(true);

        if (searchResults.length > 0) {
          toast({
            title: "Search Complete",
            description: `Found ${searchResults.length} patient records.`,
          });
        } else {
          toast({
            title: "No Results",
            description: "No patients found matching your search criteria.",
            variant: "destructive"
          });
        }
      } else {
        throw new Error('Failed to search patient data');
      }
    } catch (err) {
      toast({
        title: "Search Failed",
        description: err instanceof Error ? err.message : "Failed to search patient records. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReload = () => {
    // Clear all search state and persisted data
    setSearchTerm('');
    setSearchBy('full_name');
    setCurrentPage(1);
    setHasSearched(false);
    setPatients([]); // Clear current results
    clearPersistedSearchData();
    loadDefaultData();

    toast({
      title: "Data Reloaded",
      description: "Patient data has been refreshed.",
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      searchPatients(searchTerm, searchBy);
    } else {
      setHasSearched(false);
      loadDefaultData();
    }
  };

  // Reset to page 1 when rowsPerPage changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(1);
    }
  }, [rowsPerPage, isInitialized]);

  // Re-load data when page or rowsPerPage changes for server-side pagination
  useEffect(() => {
    if (hasuraToken && user?.type && isInitialized && !searchTerm.trim()) {
      loadDefaultData();
    }
  }, [currentPage, rowsPerPage, hasuraToken, user?.type, isInitialized]);

  return {
    patients,
    isLoading,
    searchBy,
    searchTerm,
    currentPage,
    rowsPerPage,
    totalCount,
    setSearchBy,
    setSearchTerm,
    setCurrentPage,
    setRowsPerPage,
    handleSearch,
    handleReload,
    loadDefaultData,
    searchPatients
  };
};